/**
 * TelehealthDashboard Component
 *
 * Main dashboard for telehealth sessions showing today's schedule,
 * active calls, and recent sessions
 */

import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, isToday, isTomorrow, startOfDay, endOfDay, addDays } from 'date-fns';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Video,
  Calendar,
  Clock,
  Users,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Activity,
  FileText,
  ChevronRight,
  Phone,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { telehealthSessionAPI, telehealthReportingAPI } from '@/services/api/telehealth';
import {
  TelehealthSession,
  TelehealthDashboardProps,
  SessionStatus,
} from './types';
import TelehealthSessionCard from './TelehealthSessionCard';
import SessionStatusBadge from './SessionStatusBadge';
import ScheduleTelehealthModal from './ScheduleTelehealthModal';
import { getWeaveinApiUrl } from '@/src/config';

export default function TelehealthDashboard({ className = '', onNavigate }: TelehealthDashboardProps) {
  const { fmt } = useFormattedDate();
  const [selectedView, setSelectedView] = useState<'today' | 'upcoming' | 'past'>('today');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedMemberForSchedule, setSelectedMemberForSchedule] = useState<{
    id: number;
    name: string;
  } | null>(null);

  // Listen for custom event to open schedule modal
  useEffect(() => {
    const handleOpenSchedule = () => {
      setShowScheduleModal(true);
    };

    window.addEventListener('open-schedule-telehealth', handleOpenSchedule);
    return () => {
      window.removeEventListener('open-schedule-telehealth', handleOpenSchedule);
    };
  }, []);

  // Fetch active sessions (in_progress or waiting)
  const {
    data: activeSessions,
    isLoading: loadingActive,
    refetch: refetchActive,
  } = useQuery({
    queryKey: ['telehealth-active-sessions'],
    queryFn: () => telehealthSessionAPI.getActiveSessions(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch upcoming sessions
  const {
    data: upcomingSessions,
    isLoading: loadingUpcoming,
    refetch: refetchUpcoming,
  } = useQuery({
    queryKey: ['telehealth-upcoming-sessions'],
    queryFn: () => telehealthSessionAPI.getUpcomingSessions({ days_ahead: 7 }),
  });

  // Fetch recent/completed sessions
  const {
    data: recentSessions,
    isLoading: loadingRecent,
    refetch: refetchRecent,
  } = useQuery({
    queryKey: ['telehealth-recent-sessions'],
    queryFn: () => telehealthSessionAPI.getRecentSessions({ days_back: 7 }),
  });

  // Fetch today's stats
  const today = new Date();
  const { data: todayStats, isLoading: loadingStats } = useQuery({
    queryKey: ['telehealth-today-stats', format(today, 'yyyy-MM-dd')],
    queryFn: () =>
      telehealthReportingAPI.getReportSummary(
        format(startOfDay(today), 'yyyy-MM-dd'),
        format(endOfDay(today), 'yyyy-MM-dd')
      ),
  });

  // Filter sessions for display
  const todaySessions = upcomingSessions?.filter((s) =>
    isToday(new Date(s.scheduled_start_time))
  ) || [];

  const tomorrowSessions = upcomingSessions?.filter((s) =>
    isTomorrow(new Date(s.scheduled_start_time))
  ) || [];

  const laterSessions = upcomingSessions?.filter((s) => {
    const date = new Date(s.scheduled_start_time);
    return !isToday(date) && !isTomorrow(date);
  }) || [];

  // Handle session actions
  // Navigate to a tab even when this dashboard is rendered without an onNavigate
  // prop (e.g. inside V2CalendarHub or the standalone telehealth page). app.tsx
  // exposes navigateWithHistory globally as a fallback so "Join" never no-ops.
  const navigateTab = (tab: string, id?: number) => {
    if (onNavigate) return onNavigate(tab, id != null ? { sessionId: id } : undefined);
    const globalNav = (window as any).__navigateWithHistory;
    if (typeof globalNav === 'function') return globalNav(tab);
    // Last resort: surface why nothing happened rather than failing silently.
    console.error('[telehealth] No navigation handler available for tab:', tab);
  };

  const handleJoinSession = (session: TelehealthSession) => {
    // Store session ID for the target component
    localStorage.setItem('telehealth_session_id', session.id.toString());
    // The coordinator joins the video room directly. Entering the room creates
    // the Twilio room and starts the session (backend advances scheduled/waiting
    // -> in_progress on room create), so a separate waiting-room + /start step
    // isn't required. This makes "Join" work in one click for scheduled sessions.
    navigateTab('telehealth-session', session.id);
  };

  const handleSelectSession = (session: TelehealthSession) => {
    localStorage.setItem('telehealth_session_id', session.id.toString());
    navigateTab('telehealth-session', session.id);
  };

  const refreshAll = () => {
    refetchActive();
    refetchUpcoming();
    refetchRecent();
  };

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${className}`}>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[13px] text-ink-500">
                {fmt.custom(today, 'EEEE, MMMM d, yyyy')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={refreshAll}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
              <button
                onClick={() => setShowScheduleModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Schedule Session
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Active Sessions Alert */}
        {activeSessions && activeSessions.length > 0 && (
          <div className="mb-6 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-teal-100 dark:bg-teal-900/50 rounded-lg">
                <Activity className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <h2 className="font-semibold text-teal-900 dark:text-teal-100">
                  Active Sessions
                </h2>
                <p className="text-sm text-teal-700 dark:text-teal-300">
                  {activeSessions.length} session{activeSessions.length !== 1 ? 's' : ''} in progress or waiting
                </p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {activeSessions.map((session) => (
                <div
                  key={session.id}
                  className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-teal-200 dark:border-teal-700 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <SessionStatusBadge status={session.status} size="sm" showIcon />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                        {session.member_name || `Member #${session.member_id}`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {fmt.custom(session.scheduled_start_time, 'h:mm a')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleJoinSession(session)}
                    className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-1"
                  >
                    <Video className="h-3.5 w-3.5" />
                    {session.status === 'in_progress' ? 'Rejoin' : 'Join'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <StatCard
            icon={<Calendar className="h-5 w-5" />}
            label="Today's Sessions"
            value={todaySessions.length}
            color="blue"
            loading={loadingUpcoming}
          />
          <StatCard
            icon={<CheckCircle2 className="h-5 w-5" />}
            label="Completed"
            value={todayStats?.sessions.total_completed || 0}
            color="green"
            loading={loadingStats}
          />
          <StatCard
            icon={<XCircle className="h-5 w-5" />}
            label="No Shows"
            value={todayStats?.sessions.total_no_shows || 0}
            color="red"
            loading={loadingStats}
          />
          <StatCard
            icon={<Clock className="h-5 w-5" />}
            label="Avg Duration"
            value={`${Math.round(todayStats?.sessions.avg_duration_minutes || 0)} min`}
            color="purple"
            loading={loadingStats}
          />
        </div>

        {/* Main Content */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Today's Schedule */}
          <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                  Today's Schedule
                </h2>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {todaySessions.length} session{todaySessions.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {loadingUpcoming ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Loading sessions...
                </div>
              ) : todaySessions.length === 0 ? (
                <div className="p-8 text-center">
                  <Calendar className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No sessions scheduled for today
                  </p>
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('open-schedule-telehealth'))}
                    className="mt-4 text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 text-sm font-medium"
                  >
                    Schedule a session →
                  </button>
                </div>
              ) : (
                todaySessions.map((session) => (
                  <TelehealthSessionCard
                    key={session.id}
                    session={session}
                    onClick={handleSelectSession}
                    onJoin={handleJoinSession}
                    showActions
                    compact
                    className="border-0 rounded-none shadow-none"
                  />
                ))
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tomorrow Preview */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Tomorrow</h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {tomorrowSessions.length} session{tomorrowSessions.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="p-4 space-y-3">
                {tomorrowSessions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                    No sessions scheduled
                  </p>
                ) : (
                  tomorrowSessions.slice(0, 3).map((session) => (
                    <div
                      key={session.id}
                      onClick={() => handleSelectSession(session)}
                      className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 p-2 -mx-2 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {session.is_audio_only ? (
                          <Phone className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Video className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="text-gray-500 dark:text-gray-400">
                          {fmt.custom(session.scheduled_start_time, 'h:mm a')}
                        </span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {session.member_name || `Member #${session.member_id}`}
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinSession(session);
                        }}
                        className="px-2.5 py-1 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-1 shrink-0"
                      >
                        <Video className="h-3 w-3" /> Join
                      </button>
                    </div>
                  ))
                )}
                {tomorrowSessions.length > 3 && (
                  <button className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400">
                    +{tomorrowSessions.length - 3} more
                  </button>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Quick Actions</h3>
              </div>
              <div className="p-2">
                <QuickActionButton
                  icon={<Plus className="h-4 w-4" />}
                  label="Schedule New Session"
                  onClick={() => setShowScheduleModal(true)}
                />
                <QuickActionButton
                  icon={<FileText className="h-4 w-4" />}
                  label="View Encounters"
                  onClick={() => onNavigate?.('telehealth-encounters')}
                />
                <QuickActionButton
                  icon={<Activity className="h-4 w-4" />}
                  label="View Reports"
                  onClick={() => onNavigate?.('telehealth-reports')}
                />
                <QuickActionButton
                  icon={<Users className="h-4 w-4" />}
                  label="Member Sessions"
                  onClick={() => onNavigate?.('telehealth-by-member')}
                />
              </div>
            </div>

            {/* Recent/Completed Sessions */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Recent Sessions</h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {recentSessions?.length || 0} completed
                </span>
              </div>
              <div className="p-4 space-y-3">
                {loadingRecent ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                    Loading...
                  </p>
                ) : !recentSessions || recentSessions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                    No recent sessions
                  </p>
                ) : (
                  recentSessions.slice(0, 5).map((session) => (
                    <div
                      key={session.id}
                      onClick={() => handleSelectSession(session)}
                      className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 p-2 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          session.status === 'completed'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : session.status === 'no_show'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {session.status === 'completed' ? 'Done' : session.status === 'no_show' ? 'No-show' : 'Cancelled'}
                        </span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {session.member_name || `Member #${session.member_id}`}
                        </span>
                      </div>
                      <span className="text-gray-500 dark:text-gray-400">
                        {fmt.custom(session.scheduled_start_time, 'MMM d')}
                      </span>
                    </div>
                  ))
                )}
                {recentSessions && recentSessions.length > 5 && (
                  <button
                    onClick={() => onNavigate?.('telehealth-encounters')}
                    className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 w-full text-center"
                  >
                    View all {recentSessions.length} sessions →
                  </button>
                )}
              </div>
            </div>

            {/* This Week Summary */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">This Week</h3>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Upcoming</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {upcomingSessions?.length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Later sessions</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {laterSessions.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Modal - shows member selector if no member pre-selected */}
      {showScheduleModal && (
        <MemberSelectModal
          open={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedMemberForSchedule(null);
          }}
          onSelectMember={(member) => {
            setSelectedMemberForSchedule(member);
          }}
          selectedMember={selectedMemberForSchedule}
          onScheduled={() => {
            setShowScheduleModal(false);
            setSelectedMemberForSchedule(null);
            refetchUpcoming();
          }}
        />
      )}

    </div>
  );
}

// Member Selection Modal with embedded schedule functionality
interface MemberSelectModalProps {
  open: boolean;
  onClose: () => void;
  onSelectMember: (member: { id: number; name: string }) => void;
  selectedMember: { id: number; name: string } | null;
  onScheduled: () => void;
}

function MemberSelectModal({
  open,
  onClose,
  onSelectMember,
  selectedMember,
  onScheduled,
}: MemberSelectModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ id: number; name: string }[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Search members
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id');
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (cboId) headers['X-CBO-ID'] = cboId;

        const response = await fetch(
          `${getWeaveinApiUrl('/api')}/members?search=${encodeURIComponent(searchQuery)}&limit=10`,
          { headers }
        );
        if (response.ok) {
          const data = await response.json();
          const members = (data.members || data || []).map((m: any) => ({
            id: m.id,
            name: `${m.first_name || ''} ${m.last_name || ''}`.trim() || `Member #${m.id}`,
          }));
          setSearchResults(members);
        }
      } catch (err) {
        console.error('Member search error:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  if (!open) return null;

  // If a member is selected, show the schedule modal
  if (selectedMember) {
    return (
      <ScheduleTelehealthModal
        open={true}
        onClose={onClose}
        memberId={selectedMember.id}
        memberName={selectedMember.name}
        onScheduled={onScheduled}
      />
    );
  }

  // Otherwise, show member search
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Select Member for Telehealth Session
          </h2>

          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search members by name..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              autoFocus
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="h-4 w-4 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
              </div>
            )}
          </div>

          {searchResults.length > 0 && (
            <div className="mt-3 max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              {searchResults.map((member) => (
                <button
                  key={member.id}
                  onClick={() => onSelectMember(member)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0 flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400 font-medium text-sm">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-gray-900 dark:text-gray-100">{member.name}</span>
                </button>
              ))}
            </div>
          )}

          {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              No members found matching "{searchQuery}"
            </p>
          )}

          {searchQuery.length < 2 && (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              Type at least 2 characters to search
            </p>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-850 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: 'blue' | 'green' | 'red' | 'purple' | 'yellow';
  loading?: boolean;
}

function StatCard({ icon, label, value, color, loading }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          {loading ? (
            <div className="h-7 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          ) : (
            <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Quick Action Button Component
interface QuickActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

function QuickActionButton({ icon, label, onClick }: QuickActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
    >
      <span className="text-gray-400 dark:text-gray-500">{icon}</span>
      <span className="text-sm">{label}</span>
      <ChevronRight className="h-4 w-4 ml-auto text-gray-400" />
    </button>
  );
}
